import AddCircleIcon from '@mui/icons-material/AddCircleOutline'
import RemoveIcon from '@mui/icons-material/RemoveCircleOutline'
import { IconButton, Stack, TextField, Typography } from '@mui/material'
import BigDecimal from 'js-big-decimal'
import { FC, useEffect, useState } from 'react'

interface QuantityInputProps {
  value?: number
  increment?: number
  minValue?: number
  maxValue?: number
  onChange(value: number): void
  endAdornment?: string
}

export const QuantityInput: FC<QuantityInputProps> = ({
  minValue = 0,
  maxValue = 100,
  value = 0,
  increment = 0.1,
  onChange,
  endAdornment,
}) => {
  const [valueBD, setValueBD] = useState<BigDecimal>(new BigDecimal(value))

  const [minValueBD, setMinValueBD] = useState<BigDecimal>(new BigDecimal(minValue))
  const [maxValueBD, setMaxValueBD] = useState<BigDecimal>(new BigDecimal(maxValue))
  useEffect(() => {
    setMinValueBD(new BigDecimal(minValue || 0))
  }, [minValue])
  useEffect(() => {
    setMaxValueBD(new BigDecimal(maxValue || 100))
  }, [maxValue])

  useEffect(() => {
    onChange(parseFloat(valueBD.getValue()))
  }, [valueBD])

  const validate = (inputValue: string) => {
    if (inputValue === '-') return false
    const testValueBD = new BigDecimal(inputValue)
    if (minValueBD.compareTo(testValueBD) >= 0) return false
    if (maxValueBD.compareTo(testValueBD) <= 0) return false
    return true
  }

  const addValue = (addend: number) => {
    const addendBD = new BigDecimal(addend)
    let newValue = valueBD.add(addendBD)

    if (minValueBD.compareTo(newValue) >= 0) {
      newValue = new BigDecimal(minValueBD.getValue())
    }
    if (maxValueBD.compareTo(newValue) <= 0) {
      newValue = new BigDecimal(maxValueBD.getValue())
    }
    setValueBD(newValue)
  }

  const pressMinus = () => {
    addValue(-increment)
  }
  const pressPlus = () => {
    addValue(increment)
  }

  const quantizeInputValue: (inputValue: string) => string = inputValue => {
    const valueDisplayNumber = parseFloat(inputValue)
    const numIncrements = Math.round(valueDisplayNumber / increment)
    return BigDecimal.multiply(numIncrements.toString(), increment.toString())
  }

  const onChangeText = (text: string) => {
    const matches = text.match(/-?([0-9]+)?(\.)?[0-9]*/g)
    if (!matches) return
    const _valueDisplay = matches[0]

    if (!!validate(_valueDisplay)) {
      const newValueBD = new BigDecimal(quantizeInputValue(_valueDisplay))
      setValueBD(newValueBD)
    }
  }

  return (
    <Stack direction="row" alignItems="center">
      <IconButton onClick={pressMinus} color="secondary" disabled={value === minValue}>
        <RemoveIcon />
      </IconButton>

      <TextField
        type="number"
        sx={{
          '.MuiOutlinedInput-input': {
            textAlign: endAdornment ? 'right' : 'center',
            paddingInline: 0,
            marginLeft: endAdornment ? (value >= maxValue ? 0 : '-10px') : 0,
          },
          width: '2em',
          'input::-webkit-inner-spin-button': {
            display: 'none',
            opacity: 0,
          },
          '.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'transparent',
          },
          '.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline ': {
            borderColor: 'transparent',
          },
          '.MuiOutlinedInput-notchedOutline': {
            borderColor: 'transparent',
          },
        }}
        inputProps={{ max: maxValue, min: minValue }}
        value={value}
        onChange={event => onChangeText(event.target.value)}
      />
      {!!endAdornment && (
        <Typography
          style={{
            marginLeft: value >= maxValue ? 0 : '-10px',
          }}
        >
          {endAdornment}
        </Typography>
      )}

      <IconButton onClick={pressPlus} color="secondary" disabled={value === maxValue}>
        <AddCircleIcon />
      </IconButton>
    </Stack>
  )
}
